import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { toast } from "sonner";
import { Library, Plus, Trash2, Send, X, FileText, ListChecks, Wand2, SquarePen, FileUp, Paperclip } from "lucide-react";

const SOURCE = {
  ai: { label: "AI", icon: Wand2, color: "#E07A5F" },
  manual: { label: "Built", icon: SquarePen, color: "#81B29A" },
  upload: { label: "Upload", icon: FileUp, color: "#457B9D" },
  saved: { label: "Saved", icon: Paperclip, color: "#6B7280" },
};

export default function LessonLibrary() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignItem, setAssignItem] = useState(null);
  const [groupBy, setGroupBy] = useState("subject");

  const load = () => api.get("/library").then((r) => setItems(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); api.get("/students").then((r) => setStudents(r.data)); }, []);

  const remove = async (id) => {
    if (!window.confirm("Remove this lesson from your library? (Already-assigned copies stay with students.)")) return;
    await api.delete(`/library/${id}`);
    toast.success("Removed from library.");
    setItems((x) => x.filter((i) => i.id !== id));
  };

  const groupKey = (it) => groupBy === "subject" ? (it.subject || "No subject") : (it.reading_level ? `Grade ${it.reading_level}` : "No grade");
  const grouped = {};
  items.forEach((it) => { (grouped[groupKey(it)] ||= []).push(it); });
  const groupedEntries = Object.entries(grouped).sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }));

  return (
    <div className="min-h-screen bg-[#FBF8F3]">
      <TopBar subtitle="Lesson Library" />
      <main className="max-w-5xl mx-auto px-6 md:px-8 py-10" data-testid="lesson-library-page">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 text-[#81B29A] mb-1">
              <Library size={18} />
              <span className="text-xs font-semibold uppercase tracking-[0.2em]">Reusable Lessons</span>
            </div>
            <h1 className="font-head text-4xl font-bold text-[#3D405B]">Lesson Library</h1>
            <p className="text-[#6B7280] mt-1">Build or upload a lesson once, then assign it to any child whenever you like.</p>
          </div>
          <button data-testid="library-create-btn" onClick={() => navigate("/parent/create")}
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-white font-semibold hover:opacity-90 transition-opacity" style={{ background: "#E07A5F" }}>
            <Plus size={18} /> Create a lesson
          </button>
        </div>

        {loading ? (
          <p className="text-[#6B7280]">Loading…</p>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[#EAE6DF] bg-white p-12 text-center">
            <Library size={32} className="mx-auto text-[#CBC7BF] mb-3" />
            <p className="text-[#3D405B] font-semibold">Your library is empty</p>
            <p className="text-[#6B7280] text-sm mt-1">Create a lesson, then tap "Save to Library" to reuse it later.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-5" data-testid="library-groupby">
              <span className="text-sm text-[#6B7280]">Group by</span>
              <div className="inline-flex rounded-full border border-[#EAE6DF] overflow-hidden">
                {[["subject", "Subject"], ["grade", "Grade"]].map(([k, label]) => (
                  <button key={k} data-testid={`groupby-${k}`} onClick={() => setGroupBy(k)}
                    className={`px-4 py-1.5 text-sm font-medium transition-colors ${groupBy === k ? "bg-[#81B29A] text-white" : "bg-white text-[#6B7280] hover:text-[#3D405B]"}`}>{label}</button>
                ))}
              </div>
            </div>
            <div className="space-y-8">
              {groupedEntries.map(([label, list]) => (
                <section key={label} data-testid={`library-group-${label}`}>
                  <h2 className="font-head text-lg font-semibold text-[#3D405B] mb-3 flex items-center gap-2">
                    {label} <span className="text-sm font-normal text-[#6B7280]">· {list.length}</span>
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {list.map((it) => <LibraryCard key={it.id} it={it} onAssign={setAssignItem} onRemove={remove} />)}
                  </div>
                </section>
              ))}
            </div>
          </>
        )}
      </main>

      {assignItem && (
        <AssignModal item={assignItem} students={students} onClose={() => setAssignItem(null)} />
      )}
    </div>
  );
}

function LibraryCard({ it, onAssign, onRemove }) {
  const src = SOURCE[it.source] || SOURCE.manual;
  return (
    <div data-testid={`library-item-${it.id}`} className="rounded-2xl bg-white border border-[#EAE6DF] p-5 flex flex-col">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-head text-lg font-semibold text-[#3D405B] truncate">{it.title}</p>
          <p className="text-xs text-[#6B7280] mt-0.5">{it.subject || "No subject"}{it.reading_level ? ` · Grade ${it.reading_level}` : ""}</p>
        </div>
        <span className="shrink-0 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold text-white" style={{ background: src.color }}>
          <src.icon size={12} /> {src.label}
        </span>
      </div>
      <div className="flex items-center gap-4 text-xs text-[#6B7280] mt-3">
        <span className="flex items-center gap-1"><ListChecks size={13} /> {it.question_count} question{it.question_count === 1 ? "" : "s"}</span>
        {it.attachment_count > 0 && <span className="flex items-center gap-1"><FileText size={13} /> {it.attachment_count} file{it.attachment_count === 1 ? "" : "s"}</span>}
      </div>
      <div className="flex items-center gap-2 mt-5 pt-4 border-t border-[#F1EEE8]">
        <button data-testid={`assign-btn-${it.id}`} onClick={() => onAssign(it)}
          className="flex items-center gap-1.5 rounded-full bg-[#81B29A] text-white px-4 py-2 text-sm font-semibold hover:bg-[#6f9d87] transition-colors">
          <Send size={14} /> Assign
        </button>
        <button data-testid={`delete-btn-${it.id}`} onClick={() => onRemove(it.id)}
          className="ml-auto text-[#9CA3AF] hover:text-[#E07A5F] p-2"><Trash2 size={16} /></button>
      </div>
    </div>
  );
}

function AssignModal({ item, students, onClose }) {
  const [selected, setSelected] = useState([]);
  const [due, setDue] = useState("");
  const [busy, setBusy] = useState(false);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const assign = async () => {
    if (selected.length === 0) return toast.error("Pick at least one student.");
    setBusy(true);
    try {
      const { data } = await api.post(`/library/${item.id}/assign`, { student_ids: selected, due_date: due || null, published: true });
      toast.success(`Assigned to ${data.assigned} student${data.assigned === 1 ? "" : "s"}!`);
      onClose();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div data-testid="assign-modal" className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-head text-xl font-bold text-[#3D405B]">Assign lesson</h3>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#3D405B]"><X size={20} /></button>
        </div>
        <p className="text-sm text-[#6B7280] mb-4 truncate">"{item.title}"</p>

        <label className="text-sm font-medium text-[#3D405B]">Assign to</label>
        <div className="mt-2 space-y-2">
          {students.map((s) => (
            <button key={s.id} data-testid={`assign-student-${s.id}`} onClick={() => toggle(s.id)}
              className={`w-full flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${selected.includes(s.id) ? "border-[#81B29A] bg-[#81B29A]/10" : "border-[#EAE6DF] hover:border-[#81B29A]"}`}>
              <span className={`w-5 h-5 rounded-md border-2 grid place-items-center ${selected.includes(s.id) ? "border-[#81B29A] bg-[#81B29A] text-white" : "border-[#CBC7BF]"}`}>
                {selected.includes(s.id) && "✓"}
              </span>
              <span className="text-[#3D405B] font-medium">{s.first_name}</span>
              <span className="text-xs text-[#6B7280] ml-auto">Grade {s.grade_level}</span>
            </button>
          ))}
        </div>

        <label className="text-sm font-medium text-[#3D405B] block mt-4">Due date (optional)</label>
        <input type="date" data-testid="assign-due-date" value={due} onChange={(e) => setDue(e.target.value)}
          className="mt-1 w-full rounded-xl border border-[#EAE6DF] px-3 py-2.5 outline-none focus:ring-2 focus:ring-[#81B29A]" />

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 rounded-full border border-[#EAE6DF] py-3 hover:border-[#3D405B] transition-colors">Cancel</button>
          <button data-testid="confirm-assign-btn" onClick={assign} disabled={busy}
            className="flex-1 flex items-center justify-center gap-2 rounded-full bg-[#81B29A] text-white py-3 font-semibold hover:bg-[#6f9d87] transition-colors disabled:opacity-60">
            <Send size={16} /> {busy ? "Assigning…" : "Assign"}
          </button>
        </div>
      </div>
    </div>
  );
}
